import React, { useState, useEffect } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { nameResultsAtom } from "../../store/store";
import { NameOptions, NameResult, Option } from "../../types";
import { useNavigate } from "react-router-dom";
import { useSaveName } from "../../hooks/useSaveName";
import { formStateAtom } from "../../store/store";
import { GenderList } from "../../constants/common";
import Header from "../../components/Header/Header";
import Card from "../../components/common/Card/Card";
import Input from "../../components/common/Input/Input";
import Button from "../../components/common/Button/Button";
import GenderIcon from "../../components/common/GenderIcon/GenderIcon";
import "./Records.css";

interface FilterState {
  realName: string;
  status: string[];
}

const Records: React.FC = () => {
  const { handleSaveName } = useSaveName();
  const navigate = useNavigate();
  const allResults = useAtomValue(nameResultsAtom);
  const setFormState = useSetAtom(formStateAtom);
  // 筛选状态
  const [filters, setFilters] = useState<FilterState>({
    realName: "",
    status: [],
  });

  // 添加实时联想功能
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);

  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const resultsPerPage = 10;

  // 实时联想功能的 useEffect
  const handleNameSuggestions = (value: string) => {
    if (value.length > 0) {
      // 从所有结果中筛选出匹配的原名
      const suggestions = allResults
        .filter((result) =>
          `${result.firstName} ${result.lastName}`
            .toLowerCase()
            .includes(value.toLowerCase())
        )
        .map((result) => `${result.firstName} ${result.lastName}`)
        .filter((name, index, self) => self.indexOf(name) === index) // 去重
        .slice(0, 5); // 最多显示5个建议

      setNameSuggestions(suggestions);
    } else {
      setNameSuggestions([]);
    }
  };

  // 筛选结果
  const filteredResults = allResults.filter((result) => {
    const nameMatch =
      !filters.realName ||
      `${result.firstName} ${result.lastName}`
        .toLowerCase()
        .includes(filters.realName.toLowerCase());

    const statusMatch =
      filters.status.length === 0 ||
      filters.status.includes("All") ||
      (result.status === 1 && filters.status.includes("Saved")) ||
      (result.status === 0 && filters.status.includes("Created"));

    return nameMatch && statusMatch;
  });

  // 计算分页
  const totalPages = Math.ceil(filteredResults.length / resultsPerPage);
  const paginatedResults = filteredResults.slice(
    (currentPage - 1) * resultsPerPage,
    currentPage * resultsPerPage
  );

  // 处理筛选变化
  const handleNameFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters((prev) => ({ ...prev, realName: e.target.value }));
    setCurrentPage(1); // 重置到第一页
    handleNameSuggestions(e.target.value);
  };

  const handleStatusFilterChange = (status: string) => {
    setFilters((prev) => {
      const newStatus = [...prev.status];

      if (status === "All") {
        return { ...prev, status: ["All"] };
      }

      // 如果已经选中了All，先移除它
      const allIndex = newStatus.indexOf("All");
      if (allIndex !== -1) {
        newStatus.splice(allIndex, 1);
      }

      // 切换选中状态
      const statusIndex = newStatus.indexOf(status);
      if (statusIndex === -1) {
        newStatus.push(status);
      } else {
        newStatus.splice(statusIndex, 1);
      }

      return { ...prev, status: newStatus };
    });
    setCurrentPage(1); // 重置到第一页
  };

  const resetFilters = () => {
    setFilters({ realName: "", status: [] });
    setCurrentPage(1);
  };

  // 处理页面变化
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRetry = (result: NameResult) => {
    // 提取选项信息
    const retryOptions: NameOptions = {
      styles: result.styles,
      themes: result.themes,
      meanings: result.meanings,
      phonetics: result.phonetics,
    };

    // 存储表单状态
    setFormState({
      firstName: result.firstName,
      lastName: result.lastName,
      options: retryOptions,
      gender: GenderList[result.gender],
      length: result.length,
    });

    // 导航到首页
    navigate("/");
  };

  // 渲染状态标签
  const renderStatusBadge = (status: number) => {
    if (status === 1) {
      return <span className="badge badge-saved">Saved</span>;
    }
    return <span className="badge badge-generated">Created</span>;
  };

  // 渲染标签
  const renderTags = (tags: Option[]) => {
    return tags.map((tag, index) => (
      <span key={index} className="tag">
        {tag.label}
      </span>
    ));
  };

  // 格式化日期
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);

    return `${date.toISOString().split("T")[0]} ${
      date.toTimeString().split(" ")[0]
    }`;
  };

  // 修改为固定选项，不需要过滤
  const statusOptions = ["All", "Created", "Saved"];

  return (
    <div className="records-page">
      <Header />

      <main className="records-content">
        <div className="filter-section">
          <div className="filter-controls">
            <div className="name-filter">
              <Input
                value={filters.realName}
                onChange={handleNameFilterChange}
                placeholder="Search by real name"
                className="name-filter-input"
              />
              {nameSuggestions.length > 0 && (
                <div className="suggestions">
                  {nameSuggestions.map((name, index) => (
                    <div
                      key={index}
                      className="suggestion-item"
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, realName: name }));
                        setNameSuggestions([]);
                      }}
                    >
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="status-filter">
              <div className="status-dropdown">
                <button className="status-dropdown-toggle">
                  Status{" "}
                  {filters.status.length > 0 && `(${filters.status.length})`}
                </button>
                <div className="status-dropdown-menu">
                  {statusOptions.map((status) => (
                    <label key={status} className="status-option">
                      <input
                        type="checkbox"
                        checked={filters.status.includes(status)}
                        onChange={() => handleStatusFilterChange(status)}
                      />
                      <span>{status}</span>
                    </label>
                  ))}
                </div>
              </div>

              {filters.status.length > 0 && (
                <div className="selected-filters">
                  {filters.status.map((status) => (
                    <span key={status} className="selected-filter">
                      {status}
                      <button
                        className="remove-filter"
                        onClick={() => handleStatusFilterChange(status)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <Button
              variant="secondary"
              size="md"
              className="filter-reset-btn"
              onClick={resetFilters}
            >
              Reset
            </Button>
          </div>
        </div>

        {filteredResults.length === 0 ? (
          <div className="records-empty-state">
            {" "}
            {/* 修改类名，避免与 EmptyState 组件冲突 */}
            <div className="records-empty-illustration">
              <svg
                width="120"
                height="120"
                viewBox="0 0 120 120"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect width="120" height="120" rx="60" fill="#F5F7FA" />
                <path
                  d="M40 60H80"
                  stroke="#C0C4CC"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M60 40V80"
                  stroke="#C0C4CC"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <h3>No Results Found</h3>
            <p>
              Try adjusting your filters or return to the home page to design
              new names
            </p>
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/")}
              className="records-empty-state-button"
            >
              To design new names
            </Button>
          </div>
        ) : (
          <>
            {/* 桌面视图的表格 */}
            <div className="records-table-container">
              <table className="records-table">
                <thead>
                  <tr>
                    <th>Real Name</th>
                    <th>CN Name</th>
                    <th>Tags</th>
                    <th>Explanation</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedResults.map((result) => (
                    <tr key={result.id}>
                      <td>
                        <div className="name-with-gender">
                          <span>
                            {`${result.firstName} ${result.lastName}` || "--"}
                          </span>
                          <GenderIcon gender={result.gender} />
                        </div>
                      </td>
                      <td>
                        <div className="cn-name-cell">
                          <span className="chinese-name">
                            {result.chineseName}
                          </span>
                          <span className="pinyin">{result.pinyin}</span>
                        </div>
                      </td>
                      <td>
                        <div className="tags-cell">
                          {renderTags(
                            [...result.styles, ...result.themes].slice(0, 3)
                          )}
                          {[...result.styles, ...result.themes].length > 3 && (
                            <span className="more-tag">
                              +{[...result.styles, ...result.themes].length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="explanation-cell">{result.explanation}</td>
                      <td>{renderStatusBadge(result.status)}</td>
                      <td>{formatDate(result.createdAt)}</td>
                      <td>
                        <div className="action-buttons">
                          <Button
                            variant="text"
                            size="sm"
                            onClick={() => handleRetry(result)}
                          >
                            Retry
                          </Button>
                          <Button
                            variant="text"
                            size="sm"
                            onClick={() => handleSaveName(result)}
                          >
                            {result.status === 1 ? "Unsave" : "Save"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 移动视图的卡片列表 */}
            <div className="records-cards">
              {paginatedResults.map((result) => (
                <Card key={result.id} className="record-card" elevation="sm">
                  <div className="record-card-content">
                    <div className="record-card-row">
                      <span className="record-card-label">Real Name:</span>
                      <div className="record-card-value name-with-gender">
                        <GenderIcon gender={result.gender} />
                        <span>
                          {`${result.firstName} ${result.lastName}` || "--"}
                        </span>
                      </div>
                    </div>
                    <div className="record-card-row">
                      <span className="record-card-label">CN Name:</span>
                      <div className="record-card-value">
                        <span className="chinese-name">
                          {result.chineseName}
                        </span>
                        <span className="pinyin">({result.pinyin})</span>
                      </div>
                    </div>
                    <div className="record-card-row">
                      <span className="record-card-label">Tags:</span>
                      <div className="record-card-value tags-container">
                        {renderTags(
                          [...result.styles, ...result.themes].slice(0, 3)
                        )}
                        {[...result.styles, ...result.themes].length > 3 && (
                          <span className="more-tag">
                            +{[...result.styles, ...result.themes].length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="record-card-row">
                      <span className="record-card-label">Explanation:</span>
                      <span className="record-card-value explanation-text">
                        {result.explanation}
                      </span>
                    </div>
                    <div className="record-card-row">
                      <span className="record-card-label">Status:</span>
                      <span className="record-card-value">
                        {renderStatusBadge(result.status)}
                      </span>
                    </div>
                    <div className="record-card-row">
                      <span className="record-card-label">Date:</span>
                      <span className="record-card-value">
                        {formatDate(result.createdAt)}
                      </span>
                    </div>
                    <div className="record-card-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRetry(result)}
                      >
                        Retry
                      </Button>
                      <Button
                        variant={result.status === 1 ? "secondary" : "primary"}
                        size="sm"
                        onClick={() => handleSaveName(result)}
                      >
                        {result.status === 1 ? "Unsave" : "Save"}
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* 分页控件 */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-button"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  &lt;
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                  (page) => (
                    <button
                      key={page}
                      className={`pagination-button ${
                        page === currentPage ? "active" : ""
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  )
                )}

                <button
                  className="pagination-button"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  &gt;
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Records;
